import React, { useContext, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  VisibilityOutlined,
  EditOutlined,
  PersonOutlined,
  EmailOutlined,
  BusinessOutlined,
  LocationOnOutlined,
  VerifiedOutlined,
  AdminPanelSettingsOutlined,
  BlockOutlined,
  LockOpenOutlined,
  MessageOutlined
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import SnackbarContext from '../../components/snackBar/SnackbarContext';
import { useTranslation } from 'react-i18next';

// Professional color palette with #0017E7 as primary
const colors = {
  primary: '#0017E7',         // Main brand blue
  primaryLight: '#3347FF',    // Lighter blue
  primaryDark: '#0012B8',     // Darker blue
  accent: '#0017E7',          // Accent blue
  success: '#00C853',         // Green
  warning: '#FF9800',         // Orange
  error: '#F44336',           // Red
  text: '#1A1A1A',            // Dark text
  textLight: '#616161',       // Light text
  textMuted: '#9E9E9E',       // Muted text
  surface: '#FFFFFF',         // White surface
  surfaceAlt: '#F5F7FA',      // Light gray surface
  border: '#E0E0E0',          // Border color
  white: '#FFFFFF',
};

const ProfessionalCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  borderRadius: 12,
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  overflow: 'visible',
  transition: 'all 0.3s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 56,
  height: 56,
  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
  border: `3px solid ${colors.surface}`,
  fontSize: '1.25rem',
  fontWeight: 700,
  color: colors.white,
  boxShadow: '0 4px 12px rgba(0, 23, 231, 0.2)',
}));

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  justifyContent: 'flex-end',
}));

const ProfessionalIconButton = styled(IconButton)(({ theme, variant = 'default' }) => {
  const variants = {
    view: {
      background: alpha(colors.primary, 0.08),
      color: colors.primary,
      border: `1px solid ${alpha(colors.primary, 0.2)}`,
      '&:hover': {
        background: colors.primary,
        color: colors.white,
        borderColor: colors.primary,
      }
    },
    edit: {
      background: alpha(colors.success, 0.08),
      color: colors.success,
      border: `1px solid ${alpha(colors.success, 0.2)}`,
      '&:hover': {
        background: colors.success,
        color: colors.white,
        borderColor: colors.success,
      }
    },
    block: {
      background: alpha(colors.error, 0.08),
      color: colors.error,
      border: `1px solid ${alpha(colors.error, 0.2)}`,
      '&:hover': {
        background: colors.error,
        color: colors.white,
        borderColor: colors.error,
      }
    },
    unblock: {
      background: alpha(colors.success, 0.08),
      color: colors.success,
      border: `1px solid ${alpha(colors.success, 0.2)}`,
      '&:hover': {
        background: colors.success,
        color: colors.white,
        borderColor: colors.success,
      }
    },
    message: {
      background: alpha('#2196f3', 0.08),
      color: '#2196f3',
      border: `1px solid ${alpha('#2196f3', 0.2)}`,
      '&:hover': {
        background: '#2196f3',
        color: colors.white,
        borderColor: '#2196f3',
      }
    }
  };

  return {
    width: 36,
    height: 36,
    borderRadius: 8,
    transition: 'all 0.2s ease',
    ...variants[variant],
  };
});

const StatusChip = styled(Chip)(({ status }) => {
  const statusStyles = {
    active: {
      background: alpha(colors.success, 0.1),
      color: colors.success,
      border: `1px solid ${alpha(colors.success, 0.3)}`,
      '&::before': {
        background: colors.success,
      }
    },
    pending: {
      background: alpha(colors.warning, 0.1),
      color: colors.warning,
      border: `1px solid ${alpha(colors.warning, 0.3)}`,
      '&::before': {
        background: colors.warning,
      }
    },
    inactive: {
      background: alpha(colors.error, 0.1),
      color: colors.error,
      border: `1px solid ${alpha(colors.error, 0.3)}`,
      '&::before': {
        background: colors.error,
      }
    }
  };

  return {
    height: 24,
    fontSize: '0.7rem',
    fontWeight: 600,
    borderRadius: 6,
    position: 'relative',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 8,
      top: '50%',
      transform: 'translateY(-50%)',
      width: 6,
      height: 6,
      borderRadius: '50%',
    },
    '& .MuiChip-label': {
      paddingLeft: 16,
      paddingRight: 12,
    },
    ...statusStyles[status],
  };
});

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  padding: theme.spacing(0.75, 0),
  '& .info-icon': {
    fontSize: 18,
    color: colors.textMuted,
    flexShrink: 0,
  },
  '& .info-text': {
    fontSize: '0.875rem',
    color: colors.text,
    fontWeight: 400,
    flex: 1,
    lineHeight: 1.4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const UserTypeIndicator = styled(Box)(({ userType }) => ({
  position: 'absolute',
  top: -10,
  right: 16,
  padding: '4px 12px',
  borderRadius: 6,
  background: userType === 'superadmin' 
    ? `linear-gradient(135deg, ${colors.warning} 0%, #F57C00 100%)`
    : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  color: colors.white,
  fontSize: '0.7rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  boxShadow: `0 2px 8px ${alpha(userType === 'superadmin' ? colors.warning : colors.primary, 0.3)}`,
  border: `2px solid ${colors.white}`,
}));

const HeaderBar = styled(Box)(({ userType }) => ({
  height: 4,
  background: userType === 'superadmin'
    ? `linear-gradient(90deg, ${colors.warning} 0%, #F57C00 100%)`
    : `linear-gradient(90deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
  borderRadius: '12px 12px 0 0',
}));

const ProfessionalDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(2, 0),
  background: colors.border,
  height: 1,
}));

const ModernAdminCard = ({
  item,
  onView,
  onEdit,
  showCreatedBy = true,
  userType = 'admin',
  onBlock,
  onUnblock,
  onStatusChange,
  authToken,
  onRefresh,
  onItemUpdate
}) => {
  const navigate = useNavigate();
  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last || 'U';
  };

  const normalizeIsActive = (value) => {
    if (typeof value === 'string') {
      const v = value.toLowerCase();
      if (v === 'false' || v === '0') return false;
      return true;
    }
    if (typeof value === 'number') {
      return value !== 0;
    }
    // Booleans or undefined/null
    if (value === false) return false;
    return true;
  };

  const [isActive, setIsActive] = useState(normalizeIsActive(item?.is_active));
  const { showSnackbar } = useContext(SnackbarContext);
  const { t } = useTranslation();
  const [confirmBlockOpen, setConfirmBlockOpen] = useState(false);
  const [confirmUnblockOpen, setConfirmUnblockOpen] = useState(false);
  useEffect(() => {
    const normalized = normalizeIsActive(item?.is_active);
    console.log('[ModernAdminCard] Prop change detected', {
      itemId: item?.id,
      raw_is_active: item?.is_active,
      normalized_isActive: normalized,
      userType,
    });
    setIsActive(normalized);
  }, [item?.is_active]);

  useEffect(() => {
    console.log('[ModernAdminCard] Mounted', {
      itemId: item?.id,
      raw_is_active: item?.is_active,
      initial_isActive_state: isActive,
      userType,
      apiBase,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatus = () => {
    if (isActive === false) return 'inactive';
    if (item.email_verified === false) return 'pending';
    return 'active';
  };

  const apiBase = process.env.REACT_APP_API_URL || '';
  const getTogglePath = (action) => {
    const base = userType === 'superadmin' ? 'authentication/superadmin/superadmins' : 'authentication/superadmin/admins';
    return `${apiBase}${base}/${item.id}/${action}/`;
  };

  const getAccessToken = () => {
    // Try prop, then common storage keys
    const t =
      authToken ||
      (typeof localStorage !== 'undefined' && (localStorage.getItem('access_token') || localStorage.getItem('accessToken'))) ||
      (typeof sessionStorage !== 'undefined' && (sessionStorage.getItem('access_token') || sessionStorage.getItem('accessToken')));
    return t || null;
  };

  const handleBlock = async () => {
    setConfirmBlockOpen(true);
  };

  const doBlock = async () => {
    try {
      if (onBlock) return onBlock(item);
      const token = getAccessToken();
      console.log('[ModernAdminCard] Block clicked', {
        userType,
        itemId: item?.id,
        endpoint: getTogglePath('block'),
        hasToken: Boolean(token),
        apiBase
      });
      if (!token) {
        console.warn('[ModernAdminCard] No access token found in props or storage (keys tried: access_token, accessToken).');
      }
      const res = await fetch(getTogglePath('block'), {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      console.log('[ModernAdminCard] Block response status', res.status);
      const rawText = await res.text().catch(() => '');
      console.log('[ModernAdminCard] Block response body', rawText);
      if (!res.ok) {
        throw new Error(`Failed to block: ${res.status} ${rawText}`);
      }
      let data = null;
      try {
        data = rawText ? JSON.parse(rawText) : null;
      } catch (e) {
        console.warn('[ModernAdminCard] Block response not JSON');
      }
      setIsActive(false);
      if (onStatusChange) onStatusChange({ ...item, is_active: false }, data);
      if (typeof onItemUpdate === 'function') {
        try { onItemUpdate({ ...item, is_active: false }, data); } catch (_) {}
      }
      try {
        if (showSnackbar) showSnackbar(t(userType === 'superadmin' ? 'superAdmin.modernAdminCard.superAdminBlockedSuccessfully' : 'superAdmin.modernAdminCard.adminBlockedSuccessfully'));
      } catch (_) {}
      if (typeof onRefresh === 'function') {
        try {
          await onRefresh();
        } catch (e) {
          console.warn('[ModernAdminCard] onRefresh failed after block', e);
        }
      }
    } catch (e) {
      console.error(e);
      try {
        if (showSnackbar) showSnackbar(t('superAdmin.modernAdminCard.failedToBlockAdmin'));
      } catch (_) {}
    } finally {
      setConfirmBlockOpen(false);
    }
  };

  const handleUnblock = async () => {
    setConfirmUnblockOpen(true);
  };

  const doUnblock = async () => {
    try {
      if (onUnblock) return onUnblock(item);
      const token = getAccessToken();
      console.log('[ModernAdminCard] Unblock clicked', {
        userType,
        itemId: item?.id,
        endpoint: getTogglePath('unblock'),
        hasToken: Boolean(token),
        apiBase
      });
      if (!token) {
        console.warn('[ModernAdminCard] No access token found in props or storage (keys tried: access_token, accessToken).');
      }
      const res = await fetch(getTogglePath('unblock'), {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      console.log('[ModernAdminCard] Unblock response status', res.status);
      const rawText = await res.text().catch(() => '');
      console.log('[ModernAdminCard] Unblock response body', rawText);
      if (!res.ok) {
        throw new Error(`Failed to unblock: ${res.status} ${rawText}`);
      }
      let data = null;
      try {
        data = rawText ? JSON.parse(rawText) : null;
      } catch (e) {
        console.warn('[ModernAdminCard] Unblock response not JSON');
      }
      setIsActive(true);
      if (onStatusChange) onStatusChange({ ...item, is_active: true }, data);
      if (typeof onItemUpdate === 'function') {
        try { onItemUpdate({ ...item, is_active: true }, data); } catch (_) {}
      }
      try {
        if (showSnackbar) showSnackbar(t(userType === 'superadmin' ? 'superAdmin.modernAdminCard.superAdminUnblockedSuccessfully' : 'superAdmin.modernAdminCard.adminUnblockedSuccessfully'));
      } catch (_) {}
      if (typeof onRefresh === 'function') {
        try {
          await onRefresh();
        } catch (e) {
          console.warn('[ModernAdminCard] onRefresh failed after unblock', e);
        }
      }
    } catch (e) {
      console.error(e);
      try {
        if (showSnackbar) showSnackbar(t('superAdmin.modernAdminCard.failedToUnblockAdmin'));
      } catch (_) {}
    } finally {
      setConfirmUnblockOpen(false);
    }
  };

  const formatLocation = (item) => {
    const parts = [item.city, item.postal_code, item.country].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const getStatusLabel = (status) => {
    return t(`superAdmin.modernAdminCard.statusLabels.${status}`);
  };

  const computedStatus = getStatus();
  const showBlock = computedStatus === 'active';
  const showUnblock = computedStatus === 'inactive';
  console.log('[ModernAdminCard] Render', {
    itemId: item?.id,
    isActive_state: isActive,
    email_verified: item?.email_verified,
    computedStatus,
    showBlock,
    showUnblock,
  });

  return (
    <ProfessionalCard elevation={0}>
      <HeaderBar userType={userType} />
      
      <UserTypeIndicator userType={userType}>
        {userType === 'superadmin' ? (
          <>
            <AdminPanelSettingsOutlined sx={{ fontSize: 12 }} />
            <span>{t('superAdmin.modernAdminCard.superAdmin')}</span>
          </>
        ) : (
          <>
            <PersonOutlined sx={{ fontSize: 12 }} />
            <span>{t('superAdmin.modernAdminCard.admin')}</span>
          </>
        )}
      </UserTypeIndicator>

      <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header Section */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2.5 }}>
          <UserAvatar src={item.image}>
            {getInitials(item.first_name, item.last_name)}
          </UserAvatar>
          
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: '1.1rem',
                color: colors.text,
                lineHeight: 1.3,
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.first_name} {item.last_name}
            </Typography>
            
            {item.username && (
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  color: colors.textMuted,
                  fontWeight: 500,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                @{item.username}
                <VerifiedOutlined sx={{ fontSize: 14, color: colors.primary }} />
              </Typography>
            )}

            <StatusChip
              label={getStatusLabel(computedStatus)}
              status={computedStatus}
              size="small"
            />
          </Box>
        </Box>

        {/* Contact Information */}
        <Stack spacing={0.5} sx={{ mb: showCreatedBy && item.created_by ? 2 : 2.5, flexGrow: 1 }}>
          <InfoRow>
            <EmailOutlined className="info-icon" />
            <Typography className="info-text" title={item.email}>
              {item.email}
            </Typography>
          </InfoRow>

          {item.company && userType !== 'superadmin' && (
            <InfoRow>
              <BusinessOutlined className="info-icon" />
              <Typography className="info-text" title={item.company}>
                {item.company}
              </Typography>
            </InfoRow>
          )}

          {formatLocation(item) && (
            <InfoRow>
              <LocationOnOutlined className="info-icon" />
              <Typography className="info-text" title={formatLocation(item)}>
                {formatLocation(item)}
              </Typography>
            </InfoRow>
          )}
        </Stack>

        {/* Created By Section */}
        {showCreatedBy && item.created_by && (
          <>
            <ProfessionalDivider />
            <Box sx={{ 
              background: colors.surfaceAlt, 
              padding: 1.5, 
              borderRadius: 2, 
              mb: 2,
              border: `1px solid ${colors.border}`
            }}>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: colors.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  mb: 0.5
                }}
              >
                {t('superAdmin.modernAdminCard.createdBy')}
              </Typography>
              <Typography 
                sx={{ 
                  fontSize: '0.875rem', 
                  fontWeight: 500, 
                  color: colors.text 
                }}
              >
                {item.created_by.first_name || ''} {item.created_by.last_name || ''}
              </Typography>
              {item.created_by.email && (
                <Typography 
                  sx={{ 
                    fontSize: '0.8rem', 
                    color: colors.textLight,
                    mt: 0.25
                  }}
                >
                  {item.created_by.email}
                </Typography>
              )}
            </Box>
          </>
        )}

        {/* Action Buttons */}
        <ActionButtonsContainer>
          {userType !== 'superadmin' && (
            <Tooltip
              title={t('superAdmin.modernAdminCard.companyDetails')}
              placement="top"
            >
              <ProfessionalIconButton
                variant="view"
                onClick={() => {
                  const encodedId = btoa((item?.id || '').toString());
                  navigate(`/superadmin/company/${encodedId}`);
                }}
                size="small"
              >
                <BusinessOutlined sx={{ fontSize: 18 }} />
              </ProfessionalIconButton>
            </Tooltip>
          )}
          <Tooltip
            title={t('superAdmin.modernAdminCard.viewDetails')}
            placement="top"
          >
            <ProfessionalIconButton
              variant="view"
              onClick={() => onView && onView({ ...item, is_active: isActive })}
              size="small"
            >
              <VisibilityOutlined sx={{ fontSize: 18 }} />
            </ProfessionalIconButton>
          </Tooltip>

          <Tooltip
            title={t('superAdmin.modernAdminCard.edit')}
            placement="top"
          >
            <ProfessionalIconButton
              variant="edit"
              onClick={() => onEdit && onEdit({ ...item, is_active: isActive })}
              size="small"
            >
              <EditOutlined sx={{ fontSize: 18 }} />
            </ProfessionalIconButton>
          </Tooltip>

          {/* Chat functionality commented out as per user request */}
          {/* <Tooltip
            title={t('superAdmin.modernAdminCard.chatWithAdmin')}
            placement="top"
          >
            <ProfessionalIconButton
              variant="message"
              onClick={() => navigate('/superadmin/chat', {
                state: {
                  candidate: { candidate_id: item.id }
                }
              })}
              size="small"
            >
              <MessageOutlined sx={{ fontSize: 18 }} />
            </ProfessionalIconButton>
          </Tooltip> */}

          {showBlock && (
            <Tooltip
              title={t('superAdmin.modernAdminCard.block')}
              placement="top"
            >
              <ProfessionalIconButton
                variant="block"
                onClick={handleBlock}
                size="small"
              >
                <BlockOutlined sx={{ fontSize: 18 }} />
              </ProfessionalIconButton>
            </Tooltip>
          )}

          {showUnblock && (
            <Tooltip
              title={t('superAdmin.modernAdminCard.unblock')}
              placement="top"
            >
              <ProfessionalIconButton
                variant="unblock"
                onClick={handleUnblock}
                size="small"
              >
                <LockOpenOutlined sx={{ fontSize: 18 }} />
              </ProfessionalIconButton>
            </Tooltip>
          )}
        </ActionButtonsContainer>
      </CardContent>
      <Dialog open={confirmBlockOpen} onClose={() => setConfirmBlockOpen(false)}>
        <DialogTitle>{t('superAdmin.modernAdminCard.confirmBlock')}</DialogTitle>
        <DialogContent>
          {t('superAdmin.modernAdminCard.confirmBlockMessage')}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBlockOpen(false)}>{t('superAdmin.modernAdminCard.cancel')}</Button>
          <Button color="error" variant="contained" onClick={doBlock}>{t('superAdmin.modernAdminCard.block')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmUnblockOpen} onClose={() => setConfirmUnblockOpen(false)}>
        <DialogTitle>{t('superAdmin.modernAdminCard.confirmUnblock')}</DialogTitle>
        <DialogContent>
          {t('superAdmin.modernAdminCard.confirmUnblockMessage')}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUnblockOpen(false)}>{t('superAdmin.modernAdminCard.cancel')}</Button>
          <Button color="success" variant="contained" onClick={doUnblock}>{t('superAdmin.modernAdminCard.unblock')}</Button>
        </DialogActions>
      </Dialog>
    </ProfessionalCard>
  );
};

export default ModernAdminCard;